import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "./context/AuthContext";
import { useTheme } from "./context/ThemeContext";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import WalletButton from "./components/walletButton";
import {
  FiAlignJustify,
  FiEdit,
  FiHome,
  FiUser,
  FiShoppingCart,
  FiDollarSign,
  FiClock,
  FiLogOut,
  FiX,
} from "react-icons/fi";
import { FaShoppingCart } from "react-icons/fa";
import Logo from "./logo-192.png";
import ThemeToggle from "./components/ThemeToggle";

const Navbar = () => {
  // State management
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isUpdateProfileModalOpen, setIsUpdateProfileModalOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPhoneNumber, setNewPhoneNumber] = useState("");

  // Context hooks
  const { currentUser, name, phoneNumber, updateName, updatePhoneNumber, logout } = useAuth();
  const { darkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const mobileMenuRef = useRef(null);
  const profileDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Initialize profile form
  useEffect(() => {
    if (isUpdateProfileModalOpen) {
      setNewName(currentUser?.displayName || name || "");
      setNewPhoneNumber(phoneNumber ? phoneNumber.replace("+91", "") : "");
    }
  }, [isUpdateProfileModalOpen, currentUser?.displayName, name, phoneNumber]);

  // Profile update handler
  const handleUpdateProfile = async () => {
    try {
      if (!newName && !newPhoneNumber) {
        toast.error("Please enter name or phone number");
        return;
      }

      const updates = [];
      if (newName) updates.push(updateName(newName));
      if (newPhoneNumber) updates.push(updatePhoneNumber(`+91${newPhoneNumber}`));

      await Promise.all(updates);
      setIsUpdateProfileModalOpen(false);
      toast.success("Profile updated!");
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Update failed");
    }
  };

  // Navigation items
  const navItems = [
    { path: "/home", label: "Home", icon: <FiHome /> },
    { path: "/orders", label: "Orders", icon: <FiClock /> },
    { path: "/transactions", label: "Transactions", icon: <FiDollarSign /> },
    { path: "/cart", label: "Cart", icon: <FiShoppingCart /> },
  ];

  // Hide on auth pages
  if (["/auth", "/email-verification"].includes(location.pathname)) {
    return null;
  }

  return (
    <>
      {/* Fixed Top Navbar */}
      <header
        className={`sticky top-0 left-0 right-0 z-60 ${
          darkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-900"
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo/Brand */}
            <div className="flex items-center">
              <button
                onClick={() => navigate("/home")}
                className="flex items-center space-x-2 focus:outline-none"
              >
                <img src={Logo} alt="Logo" className="h-8 w-auto" />
                <span className="text-xl font-bold hidden sm:inline">PrintKaro</span>
              </button>
            </div>

            {/* Desktop Navigation - Modified for tablet responsiveness */}
            <nav className="hidden sm:flex items-center space-x-2 md:space-x-4 lg:space-x-6">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`flex items-center space-x-1 px-2 py-2 md:px-3 rounded-md text-sm font-medium transition ${
                    location.pathname === item.path
                      ? darkMode
                        ? "bg-gray-700 text-white"
                        : "bg-gray-300 text-gray-900"
                      : darkMode
                      ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                  aria-label={item.label}
                >
                  <span className="text-lg">{item.icon}</span>
                  <span className="hidden md:inline">{item.label}</span>
                </button>
              ))}
            </nav>

            {/* Right Side Controls */}
            <div className="flex items-center space-x-3 md:space-x-4">
              {/* Theme Toggle */}
              <ThemeToggle />

              {/* Cart Button - Always visible */}
              <button
                onClick={() => navigate("/cart")}
                className={`p-2 rounded-full relative ${
                  darkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                }`}
                aria-label="Shopping Cart"
              >
                <FaShoppingCart className="text-lg" />
              </button>

              {/* Profile Dropdown */}
              <div className="relative" ref={profileDropdownRef}>
                <button
                  onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                  className={`flex items-center space-x-1 p-2 rounded-full ${
                    darkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                  }`}
                  aria-label="User menu"
                >
                  <FiUser className="text-lg" />
                  <span className="hidden md:inline text-sm max-w-[100px] truncate">
                    {currentUser?.displayName || name || "Account"}
                  </span>
                </button>

                <AnimatePresence>
                  {isProfileDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className={`absolute px-4 right-0 mt-2 w-56 rounded-md shadow-lg ${
                        darkMode ? "bg-gray-800" : "bg-white"
                      } ring-1 ring-black ring-opacity-5 focus:outline-none`}
                    >
                      <div className="py-1">
                        <div className="py-3 border-b border-gray-200 dark:border-gray-700">
                          <p className="text-sm font-medium truncate">
                            {currentUser?.displayName || name || "User"}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {currentUser?.email}
                          </p>
                        </div>

                        <WalletButton
                          isMobile={false}
                          className={`flex w-full items-center py-2 text-sm ${
                            darkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                          }`}
                        />

                        <button
                          onClick={() => {
                            setIsUpdateProfileModalOpen(true);
                            setIsProfileDropdownOpen(false);
                          }}
                          className={`flex w-full items-center py-2 text-sm ${
                            darkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                          }`}
                        >
                          <FiEdit className="mr-2" />
                          Update Profile
                        </button>

                        <button
                          onClick={logout}
                          className={`flex w-full items-center py-2 text-sm ${
                            darkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                          }`}
                        >
                          <FiLogOut className="mr-2" />
                          Sign out
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Mobile Menu Button - Only visible on small screens */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="sm:hidden p-2 rounded-full focus:outline-none transition-colors duration-200"
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              >
                {isMobileMenuOpen ? (
                  <FiX className="text-xl" />
                ) : (
                  <FiAlignJustify className="text-xl" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className={`sm:hidden fixed inset-0 top-16 z-40 flex flex-col ${
                darkMode ? "bg-gray-900" : "bg-white"
              }`}
              ref={mobileMenuRef}
            >
              <div className="container mx-auto px-4 py-4 flex-1">
                <div className="flex flex-col space-y-2 h-full">
                  {navItems.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => {
                        navigate(item.path);
                        setIsMobileMenuOpen(false);
                      }}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium ${
                        location.pathname === item.path
                          ? darkMode
                            ? "bg-gray-700 text-white"
                            : "bg-gray-100 text-gray-900"
                          : darkMode
                          ? "text-gray-300 hover:bg-gray-800"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <span className="text-lg">{item.icon}</span>
                      <span>{item.label}</span>
                    </button>
                  ))}

                  <WalletButton
                    isMobile={true}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium ${
                      darkMode
                        ? "text-gray-300 hover:bg-gray-800"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  />
                </div>
              </div>

              {/* Footer Section */}
              <div
                className={`p-4 border-t ${
                  darkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-gray-50"
                }`}
              >
                <div className="text-center">
                  <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                    © {new Date().getFullYear()} PrintKaro. All rights reserved.
                  </p>
                  <p className={`text-xs mt-1 ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
                    Version 1.0.0
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Update Profile Modal */}
      <AnimatePresence>
        {isUpdateProfileModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className={`relative w-full max-w-md p-6 mx-4 rounded-lg shadow-xl ${
                darkMode ? "bg-gray-800" : "bg-white"
              }`}
            >
              <button
                onClick={() => setIsUpdateProfileModalOpen(false)}
                className={`absolute top-4 right-4 p-2 rounded-full ${
                  darkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                }`}
              >
                ✕
              </button>

              <h2
                className={`text-2xl font-bold mb-6 ${darkMode ? "text-white" : "text-gray-900"}`}
              >
                Update Profile
              </h2>

              <div className="space-y-4">
                <div>
                  <label
                    className={`block mb-2 text-sm font-medium ${
                      darkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg border ${
                      darkMode
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                    placeholder="Enter your name"
                  />
                </div>

                <div>
                  <label
                    className={`block mb-2 text-sm font-medium ${
                      darkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Phone Number
                  </label>
                  <div className="flex">
                    <span
                      className={`inline-flex items-center px-3 rounded-l-lg border border-r-0 ${
                        darkMode
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-gray-100 border-gray-300 text-gray-900"
                      }`}
                    >
                      +91
                    </span>
                    <input
                      type="tel"
                      value={newPhoneNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                        setNewPhoneNumber(value);
                      }}
                      className={`w-full px-4 py-2 rounded-r-lg border ${
                        darkMode
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                      placeholder="Phone number"
                    />
                  </div>
                </div>
              </div>

              <button
                onClick={handleUpdateProfile}
                className={`w-full mt-6 py-2 px-4 rounded-lg font-medium ${
                  darkMode
                    ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                    : "bg-indigo-500 hover:bg-indigo-600 text-white"
                }`}
              >
                Save Changes
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
